<script lang="ts" setup>
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { VDataTable } from 'vuetify/components';
import BottleneckTableRow from './BottleneckTableRow.vue';
import BottleneckTableExpandedRow from './BottleneckTableExpandedRow.vue';
import {
    BottleneckStatus as BottleneckStatusEnum,
    type User,
    type Escalation,
} from '@/gql/graphql';

export interface BottleneckItem {
    id: string;
    name: string;
    solution: string | null;
    description: string;
    resolutionDate: string | null;
    status: BottleneckStatusEnum;
    responsible: string;
    users?: User[];
    hasActiveEscalation: boolean;
    escalations: {
        id: string;
        escalationReason: string;
        expectedSolution: string | null;
        name: string | null;
        description: string | null;
        expectedResolutionDate: string | null;
        solvedAt: string | null;
    }[];
}

export interface BottleneckTableProps {
    items: BottleneckItem[];
    itemsPerPage?: number;
    loading?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
    canEscalate?: boolean;
    columns?: string[];
}

const {
    itemsPerPage = -1,
    canUpdate = false,
    canDelete = false,
    canEscalate = false,
    columns = undefined,
    items,
    loading,
} = defineProps<BottleneckTableProps>();
const { t } = useI18n({
    messages: {
        nl: {
            solution: 'Oplossing',
            resolution_date: 'Oplossingsdatum',
            no_results: 'Geen bottlenecks om te tonen',
        },
        en: {
            solution: 'Solution',
            resolution_date: 'Resolution date',
            no_results: 'No bottlenecks to show',
        },
    },
});

const sortBy = ref([{ key: 'status', order: 'asc' as const }]);

const expanded = ref<string[]>([]);
const seen = new Set<string>();
watchEffect(() => {
    const toAdd = items
        .filter((item) => !seen.has(item.id))
        .map((item) => item.id);
    toAdd.forEach((id) => seen.add(id));
    if (toAdd.length) expanded.value = [...expanded.value, ...toAdd];
});

function sortStatus(a: BottleneckStatusEnum, b: BottleneckStatusEnum): number {
    const order = {
        [BottleneckStatusEnum.BOTTLENECK]: 0,
        [BottleneckStatusEnum.ESCALATION]: 1,
        [BottleneckStatusEnum.SOLVED]: 2,
    };

    return order[a] - order[b];
}

const headers = computed(() => {
    let _headers: Record<string, unknown>[] = [
        {
            title: t('status'),
            key: 'status',
            sort: sortStatus,
        },
        {
            title: t('name'),
            key: 'name',
        },
        {
            title: t('description'),
            key: 'description',
            sortable: false,
        },
        {
            title: t('solution'),
            key: 'solution',
            sortable: false,
        },
        {
            title: t('resolution_date'),
            key: 'resolutionDate',
        },
        {
            title: 'Escalaties',
            key: 'escalation_count',
        },
    ];

    if (columns && columns.length > 0) {
        _headers = _headers.filter((header) =>
            columns.includes(header.key as string)
        );
    }

    if (canUpdate || canDelete) {
        _headers.push({
            title: t('actions'),
            key: 'actions',
            sortable: false,
            width: '64px',
            align: 'center' as const,
        });
    }

    return _headers;
});
</script>

<template>
    <v-data-table
        v-model:sort-by="sortBy"
        v-model:expanded="expanded"
        :headers
        :items
        :items-per-page
        :loading
        :no-data-text="t('no_results')"
    >
        <template #expanded-row="{ item }">
            <BottleneckTableExpandedRow
                v-for="escalation in item.escalations as Escalation[]"
                :key="escalation.id"
                :item="escalation"
                :bottleneck="item"
                :can-update
                :can-escalate
                :columns
            />
        </template>
        <template #item="{ item, internalItem, isExpanded, toggleExpand }">
            <BottleneckTableRow
                :item
                :columns
                :can-update
                :can-delete
                :can-escalate
                :is-expanded="isExpanded(internalItem)"
                @toggle-expand="toggleExpand(internalItem)"
            />
        </template>
        <template v-if="itemsPerPage === -1" #bottom />
    </v-data-table>
</template>

<style lang="scss" scoped>
.bottleneck-table {
    &__text {
        white-space: pre-wrap;
        overflow-wrap: break-word;
    }
}
</style>
